import React, { useState } from 'react'
import { Dropdown, Menu } from 'antd'

import { AiOutlineDown } from 'react-icons/ai'

import { usePet, useToggle } from '../../modules'

import Receive from '../reusable/receive'
import Confirm from '../reusable/confirm'
import EditGroup from '../drawers/editGroup'

const purposeCopy = {
    item: {
        title: 'Billing & Inventory Groups',
        description: 'Controls billing categorization for sale items, and which store they are dispensed/debited from by default. Assign items to a group from the item’s own edit screen.',
        endpoint: 'itemGroups',
        deleteEndpoint: 'deleteItemGroup',
    },
    routing: {
        title: 'Department Queues',
        description: 'Controls which department’s queue an order appears in, and who gets notified when a new patient arrives. Assign items to a group from the item’s own edit screen.',
        endpoint: 'routingGroups',
        deleteEndpoint: 'deleteRoutingGroup',
    },
}

const GroupList = ({ purpose = 'item' }) => {
    const copy = purposeCopy[purpose] ?? purposeCopy.item

    const [selected, setSelected] = useState(null)
    const [editGroup, toggleEditGroup] = useToggle()
    const [groups, setGroups] = useState([])

    const [{ loading, error }, refetch] = usePet(copy.endpoint, null, {
        notify: true, callback: ({ data }) => setGroups(data ?? [])
    })

    const [, deleteGroup] = usePet(copy.deleteEndpoint, { id: selected?._id }, {
        notify: true, manual: true, callback: ({ }, notif, ops) => {
            notif('Group has been deleted', ops('success'))
            refetch()
        }
    })

    const create = () => {
        setSelected(null)
        toggleEditGroup()
    }

    const edit = group => {
        setSelected(group)
        toggleEditGroup()
    }

    const remove = group => {
        setSelected(group)
        Confirm({
            title: 'Delete Group?',
            message: 'Sale items assigned to this group will lose that assignment. This cannot be undone.',
            okText: 'Delete Group',
            onOk: deleteGroup,
        })
    }

    return (
        <div className="w-full">
            {
                editGroup &&
                <EditGroup
                    group={selected}
                    purpose={purpose}
                    toggle={toggleEditGroup}
                    visible={editGroup}
                    onPerform={refetch} />
            }
            <div className="sm:flex sm:justify-between sm:items-center mb-4">
                <div>
                    <h2 className="font-semibold text-gray-800 text-lg mb-1">{copy.title}</h2>
                    <p className="text-xs text-gray-500 mb-0 max-w-xl">{copy.description}</p>
                </div>
                <button onClick={create} className="btn hisani-btn-green-light text-white whitespace-nowrap">
                    New Group
                </button>
            </div>
            <div className="col-span-full xl:col-span-8 bg-white shadow-lg rounded-sm border border-gray-200">
                <div className="p-3">
                    <Receive progress loading={loading} error={error}>
                        {
                            groups.length > 0 ?
                                <table className="table-auto w-full">
                                    <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                        <tr>
                                            <th className="p-2"><div className="font-semibold text-left">Name</div></th>
                                            {purpose === 'item' && <th className="p-2"><div className="font-semibold text-center">Default Store</div></th>}
                                            {purpose === 'routing' && <th className="p-2"><div className="font-semibold text-center">Hidden</div></th>}
                                            <th className="p-2"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-sm font-medium divide-y divide-gray-100">
                                        {
                                            groups.map(group =>
                                                <tr key={group._id}>
                                                    <td className="p-2">{group.name}</td>
                                                    {purpose === 'item' && <td className="p-2"><div className="text-center">{group.defaultStore?.name ?? '—'}</div></td>}
                                                    {purpose === 'routing' && <td className="p-2"><div className="text-center">{group.hidden ? 'Yes' : 'No'}</div></td>}
                                                    <td className="p-2 text-center">
                                                        <Dropdown
                                                            overlay={
                                                                <Menu>
                                                                    <Menu.Item onClick={() => edit(group)}>Edit</Menu.Item>
                                                                    <Menu.Item onClick={() => remove(group)}>Delete</Menu.Item>
                                                                </Menu>
                                                            }>
                                                            <button type="button" className="ant-dropdown-link flex items-center h-10">
                                                                <span className="mr-2">Actions</span> <AiOutlineDown />
                                                            </button>
                                                        </Dropdown>
                                                    </td>
                                                </tr>
                                            )
                                        }
                                    </tbody>
                                </table>
                                :
                                <div className="w-full h-16 flex items-center justify-center">
                                    <p className="font-semibold text-base">No groups have been created yet</p>
                                </div>
                        }
                    </Receive>
                </div>
            </div>
        </div>
    )
}

export default GroupList
